/**
 * StepDone — final step of the wizard.
 *
 * Shows a mini preview of the created CV, the current ATS score, and two CTAs:
 * - "Voir mon CV" → go to editor in simple mode
 * - "Adapter pour une offre" → go to editor and open the adapt modal (Phase 1.5)
 */

import { CheckCircle2, ArrowRight, Target, BarChart2 } from 'lucide-react';
import { useMemo } from 'react';
import { analyzeCV } from '../../../lib/ats-analyzer';
import type { CVData } from '../../../types';
import type { CommonInfo } from '../../../hooks/useCvState';

interface StepDoneProps {
  data: CVData;
  commonInfo: CommonInfo;
  onFinish: (openAdapt?: boolean) => void;
}

export function StepDone({ data, commonInfo, onFinish }: StepDoneProps) {
  const fullName =
    `${commonInfo.firstName || ''} ${commonInfo.lastName || ''}`.trim() ||
    commonInfo.fullName ||
    'Ton CV';

  const atsScore = useMemo(() => analyzeCV(data).score, [data]);
  const scoreColor = atsScore >= 70 ? 'text-emerald-400' : atsScore >= 50 ? 'text-amber-400' : 'text-red-400';
  const ringColor = atsScore >= 70 ? 'border-emerald-500/40' : atsScore >= 50 ? 'border-amber-500/40' : 'border-red-500/40';

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center size-14 rounded-full bg-emerald-500/20 text-emerald-400 mb-2">
          <CheckCircle2 className="size-7" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Ton CV est prêt</h1>
        <p className="text-base text-white/60 max-w-lg mx-auto">
          Tu peux le visualiser, l'éditer, l'adapter à une offre ou l'exporter
          dès maintenant depuis l'éditeur.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {/* Mini CV summary card */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-white/40 mb-0.5">Nom</div>
            <div className="text-lg font-bold">{fullName}</div>
          </div>
          {data.personalInfo.title && (
            <div>
              <div className="text-xs uppercase tracking-wider text-white/40 mb-0.5">Poste visé</div>
              <div className="text-sm text-white/90">{data.personalInfo.title}</div>
            </div>
          )}
          <div className="space-y-2 text-sm">
            {commonInfo.email && (
              <div>
                <div className="text-xs uppercase tracking-wider text-white/40 mb-0.5">Email</div>
                <div className="text-white/80 truncate">{commonInfo.email}</div>
              </div>
            )}
            {commonInfo.city && (
              <div>
                <div className="text-xs uppercase tracking-wider text-white/40 mb-0.5">Ville</div>
                <div className="text-white/80">{commonInfo.city}</div>
              </div>
            )}
          </div>
        </div>

        {/* ATS score */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 flex flex-col items-center justify-center gap-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-white/50 uppercase tracking-wider">
            <BarChart2 className="size-4" />
            Score ATS
          </div>
          <div className={`flex flex-col items-center justify-center size-24 rounded-full border-4 ${ringColor}`}>
            <span className={`text-4xl font-black ${scoreColor}`}>{atsScore}</span>
            <span className="text-xs text-white/40">/ 100</span>
          </div>
          <p className="text-xs text-center text-white/40">
            {atsScore >= 70 ? 'Excellent — votre CV passe bien les ATS.' : atsScore >= 50 ? 'Moyen — adaptez à l\'offre pour améliorer.' : 'À améliorer — adaptez à une offre pour progresser.'}
          </p>
        </div>
      </div>

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => onFinish(true)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 transition-all"
        >
          <Target className="size-4" />
          Adapter pour une offre maintenant
        </button>
        <button
          type="button"
          onClick={() => onFinish(false)}
          className="flex items-center gap-2 px-6 py-3 rounded-xl text-base font-bold bg-blue-600 text-white hover:bg-blue-500 shadow-xl shadow-blue-600/30 transition-all"
        >
          Voir mon CV
          <ArrowRight className="size-5" />
        </button>
      </div>
    </div>
  );
}
